import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar.jsx';
import Loader from './components/Loader.jsx';
import SEOHelmet from './components/SEOHelmet.jsx';
import SkipToContent from './components/SkipToContent.jsx';

import { initGA } from './utils/analytics';
import { initPerformanceOptimizations } from './utils/performance';
import { initAdvancedPerformance } from './utils/advancedPerformance';
import './App.css';

// Lazy load components for better performance
const Hero = lazy(() => import('./components/Hero.jsx'));
const About = lazy(() => import('./components/About.jsx'));
const Skills = lazy(() => import('./components/Skills.jsx'));
const Projects = lazy(() => import('./components/Projects.jsx'));
const Experience = lazy(() => import('./components/Experience.jsx'));
const Contact = lazy(() => import('./components/Contact.jsx'));
const Footer = lazy(() => import('./components/Footer.jsx'));


function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    
    // Initialize performance optimizations
    initPerformanceOptimizations();
    
    // Initialize advanced performance optimizations
    initAdvancedPerformance();
    
    // Preload critical components for better performance
    const preloadComponents = () => {
      import('./components/Hero.jsx');
      import('./components/About.jsx');
    };
    
    // Start preloading immediately for better LCP
    preloadComponents();

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Handle loading completion from Loader component
  const handleLoadingComplete = () => {
    setLoading(false);
  };

  return (
    <Router>
      <SEOHelmet />
      <SkipToContent />
      <div className="App bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 min-h-screen overflow-x-hidden">
        {loading ? (
          <Loader finishLoading={handleLoadingComplete} />
        ) : (
          <>
            <Navbar />
            <main id="main-content" tabIndex={-1} role="main">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }} // Reduced from 0.5s for better LCP
              >
                <Suspense fallback={<div>Loading Hero...</div>}>
                  <Hero />
                </Suspense>
                <Suspense fallback={<div>Loading About...</div>}>
                  <About />
                </Suspense>

                <Suspense fallback={<div>Loading Skills...</div>}>
                  <Skills />
                </Suspense>
                <Suspense fallback={<div>Loading Projects...</div>}>
                  <Projects />
                </Suspense>
                <Suspense fallback={<div>Loading Experience...</div>}>
                  <Experience />
                </Suspense>
                <Suspense fallback={<div>Loading Contact...</div>}>
                  <Contact />
                </Suspense>
              </motion.div>
            </main>
            <Suspense fallback={<div>Loading Footer...</div>}>
              <Footer />
            </Suspense>
            

          </>
        )}
      </div>
    </Router>
  );
}

export default App;
